import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { z } from "zod";

import { ProfilesPage } from "@/features/Profiles/ProfilesPage";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 50, 100] as const;

/** ================== URL Search State (typed) ================== */
export type ProfilesSearch = {
  search?: string; // search query (min 3 chars for API)
  page: number; // 1-based page index
  pageSize: number; // rows per page (10, 20, 30, 50, 100)
  orderBy?: string;
  order?: "asc" | "desc";
};

export const SearchSchema = z
  .object({
    search: z.string().optional(),
    page: z
      .union([z.string(), z.number()])
      .transform((v) => (typeof v === "string" ? Number(v) : v))
      .pipe(z.number().int().positive())
      .catch(1),
    pageSize: z
      .union([z.string(), z.number()])
      .transform((v) => (typeof v === "string" ? Number(v) : v))
      .pipe(z.number().int().positive())
      .transform((v) =>
        ROWS_PER_PAGE_OPTIONS.includes(
          v as (typeof ROWS_PER_PAGE_OPTIONS)[number],
        )
          ? v
          : 20,
      )
      .catch(20),
  })
  .extend({
    orderBy: z.string().optional(),
    order: z.enum(["asc", "desc"]).optional(),
  })
  .transform(
    (s): ProfilesSearch => ({
      search: s.search,
      page: Math.max(1, s.page),
      pageSize: ROWS_PER_PAGE_OPTIONS.includes(
        s.pageSize as (typeof ROWS_PER_PAGE_OPTIONS)[number],
      )
        ? s.pageSize
        : 20,
      orderBy: s.orderBy,
      order: s.order,
    }),
  );

/** ================== Route ================== */
export const Route = createFileRoute("/_auth/people/$siteId/profiles/")({
  validateSearch: (raw): ProfilesSearch => SearchSchema.parse(raw),
  component: ProfilesPageRoute,
});

function ProfilesPageRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const setSearchParams = useCallback(
    (updates: Partial<ProfilesSearch>) => {
      void navigate({
        search: (prev): ProfilesSearch => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <ProfilesPage
      searchParams={search}
      onSearchParamsChange={setSearchParams}
    />
  );
}
